import { marked } from 'marked';

// Resume content as context (extracted from PDF)
const RESUME_CONTEXT = `
Sabtian Adiansyah
Business Analyst | Business System Analyst
Location: Jakarta, Indonesia
Phone: +62 857 1630 0647
Email: sabtianadiansyah@gmail.com
LinkedIn: linkedin.com/in/sabtianadiansyah
GitHub: github.com/sabtianady

PROFESSIONAL SUMMARY:
Business Analyst with 5+ years of experience in data analysis, dashboard automation, and operational optimization. Proven track record in delivering business insights, optimizing workflows, automating processes, and leading data-driven projects using Looker Studio, SQL, Python, n8n, and AI Agents.

TECHNICAL SKILLS:
● Data & Analytics: Google Sheets, Google Analytics, SQL, Python,, Google Big Query (GCP).
● Automation: n8n, AI Agents, Python.
● Dashboards & Reporting: Looker Studio, Google Slides, Google Sheets, Canva.
● Lead Generation: Data Scraping, Apollo, SalesQL
● Soft Skills: Critical Thinking, Communication, Problem Solving, Research

WORK EXPERIENCE:
1. Dealls (PT SejutaCita Anak Muda Indonesia)
   Position: Business Analyst | Business System Analyst
   Period: October 2024 - Present
   Location: Jakarta, Indonesia

   Key Contributions:
   ● Designed and automated dashboards using Looker Studio, Google Sheets, SQL, and Python to improve cross-functional data visibility.
   ● Performed end-to-end data analysis on user acquisition, job postings, applications, and marketing trends to support strategic decision-making.
   ● Delivered actionable insights and comprehensive reports to internal stakeholders.
   ● Standardized and cleaned multi-source data to ensure accuracy and consistency.
   ● Improved lead generation through data scraping and analyzing client needs to enhance sales outreach and talent fulfillment.
   ● Collaborated with cross-functional teams to optimize workflows, resolve data discrepancies, and improve operational efficiency.
   ● Utilized Python, SQL, Google Analytics, Google Big Query (GCP), and Google Spreadsheets for data management and reporting.
   ● Created automation workflows using n8n and Artificial Intelligence (AI) agents to streamline internal processes and reduce manual tasks.
   ● Experienced in applying Large Language Models (LLMs) with Retrieval-Augmented Generation (RAG) techniques and integrating vector databases to optimize AI-driven solutions.
   ● Implemented and managed LLM observability tools to monitor model behavior and performance, optimized chatbot models for latency, token efficiency, accuracy, and relevance, and translated business and user requirements into technical LLM capabilities.
   ● Automated meeting transcript summarization & analysis (via Fireflies)

2. Ruangguru
   Position: Business Analyst
   Period: March 2020 - October 2024
   Location: Jakara, Indonesia

   Key Contributions:
   ● Analyzed operational data and provided insights to optimize tutoring systems, support R&D, and improve business performance.
   ● Led NPS, engagement surveys, and training to boost teacher satisfaction.
   ● Built dashboards and reports for strategic planning and project monitoring.
   ● Collaborated with cross-functional teams to support business development through data analysis and strategic reporting.
   ● Gathered and analyzed data to deliver insights and actionable recommendations across multiple projects.
   ● Developed Employer Value Proposition (EVP) and led branding initiatives to attract talent.
   ● Conducted research on industry trends, competitor branding strategies, and best practices to refine branding efforts.
   ● Monitored key branding metrics and reported outcomes to senior management for continuous optimization

EDUCATION:
Bina Nusantara University
Bachelor of Economics, Major in Management
Jakarta, Indonesia | 2014 – 2018

Campus activities:
● Mentorship at Teach for Indonesia
● Volunteer at Teach for Indonesia

PROJECTS:
AI Automation using n8n:
● Instagram, Telegram, & LinkedIn Contact Leads Scraper
● Automated meeting transcript summarization & analysis (via Fireflies)
● WhatsApp AI Chatbot & Instagram AI Chatbot (via ManyChat)
  ○ Able to process multiple message types (text, images, audio, documents)
  ○ Retrieval-Augmented Generation (RAG)
  ○ Respond using internal knowledge base integrated with vector databases
  ○ Ticketing workflow & Round-Robin assignment
  ○ Create Google Calendar schedules directly from chatbot messages

Internal Process Automation (Python / n8n):
● WhatsApp automated blast messaging & follow-ups
● CV Parsing using Python
● Data Categorization Automation
● WhatsApp Group Contact Scraper

Dashboards:
● Google Looker Studio Dashboards
● Google Sheets Interactive Dashboards (pivot tables, slicers, visual reports)

ACHIEVEMENTS:
● Reduced manual operational and administrative tasks by 80% through AI automation, significantly improving efficiency and productivity
● Awarded "Best Performance Employee" in the Commercial team at Dealls (Q1 2025).
● Achieved a 95% satisfaction rate in the teacher engagement survey (Q2 2024).
● Scored 65% NPS, exceeding the target of 50% (Q3 2024).
● Awarded "Chief of the Quarter" in the Business team at Ruangguru (Q4 2021).
● Opened 5 Brain Academy branches within 3 months (Q3 2021).

EDUCATION:
Bina Nusantara University
Bachelor of Economics, Major in Management
Jakarta, Indonesia | 2014 – 2018

SALARY EXPECTATIONS:
● IDR: Rp 11,000,000 - Rp 15,000,000 per month nett
● Negotiable
● IDR: Rp 100,000 / hour for Freelance

CERTIFICATIONS & PUBLICATIONS:
● Website Portfolio www.sabtian.my.id (ask AI Agents anything about my resume)
● Ruangguru Leadership Academy, July 2022
● Google Data Studio and Microsoft Project Bootcamp by Skill Academy (Ruangguru), April 2020
● Published articles on Medium and blog.ruangguru.com
● Content featured on LinkedIn and Instagram (@Akademia by Ruangguru)

PERSONAL INTERESTS:
- Technology innovation and emerging trends
- System optimization and performance tuning
- Exploring new frameworks
`;

class OpenAIService {
  constructor() {
    // tidak perlu client / initialize
  }

  parseMarkdown(content) {
    try {
      marked.setOptions({
        breaks: true,
        gfm: true,
        sanitize: false,
        smartLists: true,
        smartypants: true
      });
      return marked(content);
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return content;
    }
  }

  async askQuestion(question) {
    const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
    const baseURL = process.env.REACT_APP_MODEL_PROVIDER || 'https://openrouter.ai/api/v1';
    const model = process.env.REACT_APP_MODEL_NAME || 'google/gemma-3-4b-it:free';

    if (!apiKey) {
      console.error('Missing REACT_APP_OPENROUTER_API_KEY');
      throw new Error('Missing API key');
    }

    try {
      const res = await fetch(`${baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://sabtian.my.id',
          'X-Title': 'sabtian-portfolio'
        },
        body: JSON.stringify({
          model,
          messages: [
            {
              role: 'system',
              content: `You are an AI assistant representing Sabtian Adiansyah, a skilled Business Analyst. Use the following resume information to answer questions about Sabtian's background, skills, experience, education, etc. Be professional, friendly, and informative. If asked about something not in the resume, politely mention that you don't have that specific information but can discuss what's available in the resume or contact Sabtian from Contacts Information. Format your responses using markdown for better readability (use headers, lists, bold text, etc. where appropriate).\n\nResume Information:\n${RESUME_CONTEXT}`
            },
            {
              role: 'user',
              content: question
            }
          ]
        })
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('OpenRouter error response:', res.status, errText);
        throw new Error(`OpenRouter error: ${res.status}`);
      }

      const data = await res.json();
      const rawContent = data.choices?.[0]?.message?.content || '';

      return {
        raw: rawContent,
          formatted: this.parseMarkdown(rawContent)
      };
    } catch (error) {
      console.error('Error calling OpenRouter API:', error);
      throw new Error('Failed to get response from AI assistant');
    }
  }

  // kalau streaming belum perlu, ini bisa di-skip dulu
  async askQuestionStream() {
    throw new Error('Streaming not implemented yet');
  }
}

export default new OpenAIService();
